import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { Group } from '../models';
import { BodyEncoder, Globals } from './../helpers';
import { HttpInterceptor } from './http-error.interceptor';

@Injectable()
export class GroupService {
  private groupServiceUrl: string;

  constructor(private http: HttpInterceptor, private globals: Globals) {
    this.groupServiceUrl = globals.url + 'groups/';
  }

  public getAll() {
    return this.http
      .get(this.groupServiceUrl)
      .map((response: Response) => response.json());
  }

  public getAllinTree() {
    return this.http
      .get(this.groupServiceUrl + '?tree=1')
      .map((response: Response) => response.json());
  }

  public getById(groupId: string) {
    return this.http
      .get(this.groupServiceUrl + groupId)
      .map((response: Response) => response.json());
  }

  public create(group: Group) {
    return this.http
      .post(this.groupServiceUrl, group)
      .map((response: Response) => response.json());
  }
  public delete(groupId: string) {
    return this.http
      .delete(this.groupServiceUrl + groupId)
      .map((response: Response) => response.json());
  }
  public update(groupId: string, group: Group) {
    return this.http
      .put(this.groupServiceUrl + groupId, group)
      .map((response: Response) => response.json());
  }

  /* Devices */
  public getGroupDevices(groupId: string, page?, limit?, order?, filters?) {
    let url = this.groupServiceUrl + groupId + '/devices';
    const params: URLSearchParams = new URLSearchParams();
    if (filters && Object.keys(filters).length !== 0) {
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
    }
    if (typeof page !== 'undefined' && typeof limit !== 'undefined') {
      params.set('page', page + '');
      params.set('limit', limit + '');
    }
    if (order && Object.keys(order).length !== 0) {
      Object.keys(order).forEach((filter) => {
        params.set(filter, order[filter] + '');
      });
    }
    const paramsStr = params.toString();
    if (paramsStr) { url += '?' + paramsStr; }
    // console.log("paramsStr", paramsStr);

    return this.http.get(url).map((response: Response) => response.json());
  }

  public getGroupContents(groupId: string, filters?) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/contents', filters);
    return this.http.get(url).map((response: Response) => response.json());
  }
  public getGroupC3Apps(groupId: string, filters?) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/c3apps', filters);
    return this.http.get(url).map((response: Response) => response.json());
  }
  public getGroupUsers(groupId: string, filters?) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/users', filters);
    return this.http.get(url).map((response: Response) => response.json());
  }
  public getGroupData(groupId: string, filters?) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/data', filters);
    return this.http.get(url).map((response: Response) => response.json());
  }

  public addDeviceToGroup(groupId: string, deviceId: string) {
    return this.http
      .put(this.groupServiceUrl + groupId + '/devices/' + deviceId, null)
      .map((response: Response) => response.json());
  }
  public addDevicesToGroup(groupId: string, devices: string[]) {
    return this.http
      .put(this.groupServiceUrl + groupId + '/devices', { devices })
      .map((response: Response) => response.json());
  }
  public removeDeviceFromGroup(groupId: string, deviceId: string) {
    return this.http
      .delete(this.groupServiceUrl + groupId + '/devices/' + deviceId, null)
      .map((response: Response) => response.json());
  }

  /* Contents */
  public addContentToGroup(groupId: string, contentId: string) {
    return this.http
      .put(this.groupServiceUrl + groupId + '/contents/' + contentId, null)
      .map((response: Response) => response.json());
  }
  public addContentsToGroup(groupId: string, contents: string[], deviceFilters) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/contents', deviceFilters);
    return this.http.put(url, { contents }).map((response: Response) => response.json());
  }
  public removeContentFromGroup(groupId: string, contentId: string, deviceFilters?) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/contents/' + contentId, deviceFilters);
    return this.http.delete(url).map((response: Response) => response.json());
  }
  public checkContents(groupId: string) {
    return this.http
      .get(this.groupServiceUrl + groupId + '/sameContents')
      .map((response: Response) => response.json());
  }
  /* C3 Apps */
  public devicesMoreThanSixC3Apps(groupId: string, c3apps: string[], deviceFilters) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/c3apps-verify', deviceFilters);
    return this.http.put(url, { c3apps }).map((response: Response) => response.json());
  }
  public addC3AppsToGroup(groupId: string, c3apps: string[], deviceFilters) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/c3apps', deviceFilters);
    return this.http.put(url, { c3apps }).map((response: Response) => response.json());
  }
  public removeC3AppFromGroup(groupId: string, c3AppId: string, deviceFilters?) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/c3apps/' + c3AppId, deviceFilters);
    return this.http.delete(url).map((response: Response) => response.json());
  }

  /* Users */
  public addUsersToGroup(groupId: string, users: string[], deviceFilters) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/users', deviceFilters);
    return this.http.put(url, { users }).map((response: Response) => response.json());
  }
  public removeUserFromGroup(groupId: string, userId: string, deviceFilters?) {
    const url = this.prepareUrlFilters(this.groupServiceUrl + groupId + '/users/' + userId, deviceFilters);
    return this.http.delete(url).map((response: Response) => response.json());
  }

  private prepareUrlFilters(url, filters) {
    const params: URLSearchParams = new URLSearchParams();
    if (!filters || !Object.keys(filters).length) { return url; }
    Object.entries(filters).forEach(([key, value]) => {
      params.append(key, value.toString());
    });
    const paramsStr = params.toString();
    if (paramsStr) { url += '?' + paramsStr; }
    return url;
  }
}
